import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useCubeQuery } from '@cubejs-client/react';
import { Spin, Row, Col, Statistic, Table } from 'antd';
import {
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts';
import { useDeepCompareMemo } from 'use-deep-compare';



const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Strip cube/view name from column title so only field name is shown (e.g. "Transactions view Amount" -> "Amount")
function columnTitleWithoutView(column) {
  const key = column.key ?? column.dataIndex ?? '';
  let title = column.title ?? '';
  if (!title) return title;

  // Build possible prefixes from cube key (e.g. "Transactions_view" -> "Transactions view ")
  if (typeof key === 'string' && key.includes('.')) {
    const [cubePart] = key.split('.');
    const viewPrefix = cubePart.replace(/_/g, ' ').trim() + ' ';
    if (viewPrefix.length > 1 && title.startsWith(viewPrefix)) {
      return title.slice(viewPrefix.length);
    }
  }

  // Fallback: strip common "ViewName " pattern case-insensitively (title may be "Transactions View Amount")
  const viewLabel = typeof key === 'string' && key.includes('.')
    ? key.split('.')[0].replace(/_/g, ' ').trim()
    : null;
  if (viewLabel) {
    const prefix = viewLabel + ' ';
    if (title.length > prefix.length && title.toLowerCase().startsWith(prefix.toLowerCase())) {
      return title.slice(prefix.length);
    }
  }

  // Last resort: if title contains " view " (cube naming pattern), take the part after it
  const viewIdx = title.indexOf(' view ');
  if (viewIdx !== -1) {
    const after = title.slice(viewIdx + 6).trim(); // after " view "
    if (after.length > 0) title = after;
  }

  if (typeof title === 'string') {
    title = title.replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/g, '');
  }

  return title;
}

// Map column tree so each column's title is the field name only (no view prefix)
function tableColumnsWithShortTitles(columns) {
  if (!columns || !columns.length) return columns;
  return columns.map((col) => {
    const next = { ...col, title: columnTitleWithoutView(col) };
    if (next.children && next.children.length) {
      next.children = tableColumnsWithShortTitles(next.children);
    }
    return next;
  });
}

// try {
//   // Avoid circular structures
//   console.log('Cube query:', JSON.parse(JSON.stringify(query)));
// } catch (e) {
//   console.log('Cube query (raw):', query);
// }


// Strip timestamps for charting components
const formatTimeString = (str) => {
  if (typeof str === 'string') {
    return str.replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/g, '');
  }
  return str;
};

const formatChartPivot = (pivotData) => {
  return pivotData.map(row => {
    const next = { ...row };
    if (next.x) next.x = formatTimeString(next.x);
    return next;
  });
};

const getTooltipFormatter = (resultSet) => {
  if (!resultSet || !resultSet.tableColumns) return undefined;
  
  const columns = resultSet.tableColumns();
  function flatten(cols = []) {
    return cols.reduce((memo, column) => {
      if (column.children) {
        return [...memo, ...flatten(column.children)];
      }
      return [...memo, column];
    }, []);
  }
  const currencyTitles = flatten(columns)
    .filter(col => col.format === 'currency' || col.type === 'currency' || (col.type === 'number' && (col.format === 'money' || col.format === 'usd')))
    .map(col => formatTimeString(columnTitleWithoutView(col)));

  return (value, name) => {
    // Determine if it should be formatted as currency
    const isCurrency = currencyTitles.includes(name) || (
      // For single measure charts like Pie, name might be the category and not the measure name
      resultSet.seriesNames().length === 1 && 
      currencyTitles.includes(formatTimeString(columnTitleWithoutView(resultSet.seriesNames()[0])))
    );

    if (isCurrency && typeof value === 'number') {
      return [usdFormatter.format(value), name];
    }
    return [value, name];
  };
};

const CartesianChart = ({ resultSet, children, ChartComponent }) => (
  <ResponsiveContainer width="100%" height={350}>
    <ChartComponent data={formatChartPivot(resultSet.chartPivot())}>
      <XAxis dataKey="x" />
      <YAxis />
      <CartesianGrid />
      {children}
      <Legend />
      <Tooltip cursor={{ fill: 'rgba(128, 128, 128, 0.15)' }} formatter={getTooltipFormatter(resultSet)} />
    </ChartComponent>
  </ResponsiveContainer>
);
const colors = ['#2A2B5F', '#7E2C6A', '#D9385E', '#F37946', '#F9C053', '#FDD983'];
const stackedChartData = (resultSet) => {
  const data = resultSet
    .pivot()
    .map(({ xValues, yValuesArray }) =>
      yValuesArray.map(([yValues, m]) => ({
        x: resultSet.axisValuesString(xValues, ', '),
        color: resultSet.axisValuesString(yValues, ', '),
        measure: m && Number.parseFloat(m),
      }))
    )
    .reduce((a, b) => a.concat(b), []);
  return data;
};
const formatTableData = (columns, data) => {
  function flatten(columns = []) {
    return columns.reduce((memo, column) => {
      if (column.children) {
        return [...memo, ...flatten(column.children)];
      }
      return [...memo, column];
    }, []);
  }
  const typeByIndex = flatten(columns).reduce((memo, column) => {
    return {
      ...memo,
      [column.dataIndex]: column,
    };
  }, {});
  function formatValue(value, { type, format } = {}) {
    if (value == undefined) {
      return value;
    }
    if (type === 'time') {
      if (typeof value === 'string' && value.includes('T')) {
        return value.split('T')[0];
      }
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) { }
    }
    if (type === 'boolean') {
      if (typeof value === 'boolean') {
        return value.toString();
      } else if (typeof value === 'number') {
        return Boolean(value).toString();
      }
      return value;
    }
    if (type === 'number' && format === 'percent') {
      return [parseFloat(value).toFixed(2), '%'].join('');
    }
    if (
      format === 'currency' ||
      type === 'currency' ||
      (type === 'number' && (format === 'money' || format === 'usd'))
    ) {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(n)) {
        return usdFormatter.format(n);
      }
      return value.toString();
    }
    return value.toString();
  }
  function format(row) {
    return Object.fromEntries(
      Object.entries(row).map(([dataIndex, value]) => {
        return [dataIndex, formatValue(value, typeByIndex[dataIndex])];
      })
    );
  }
  return data.map(format);
};
const TableRenderer = ({ resultSet }) => {
  const [tableColumns, dataSource] = useDeepCompareMemo(() => {
    const columns = resultSet.tableColumns();
    const columnsShortTitles = tableColumnsWithShortTitles(columns);
    return [
      columnsShortTitles,
      formatTableData(columns, resultSet.tablePivot()),
    ];
  }, [resultSet]);
  return (
    <Table pagination={false} columns={tableColumns} dataSource={dataSource} />
  );
};
const TypeToChartComponent = {
  line: ({ resultSet }) => {
    return (
      <CartesianChart resultSet={resultSet} ChartComponent={LineChart}>
        {resultSet.seriesNames().map((series, i) => (
          <Line
            key={series.key}
            stackId="a"
            dataKey={series.key}
            name={formatTimeString(columnTitleWithoutView(series))}
            stroke={colors[i]}
          />
        ))}
      </CartesianChart>
    );
  },
  bar: ({ resultSet }) => {
    return (
      <CartesianChart resultSet={resultSet} ChartComponent={BarChart}>
        {resultSet.seriesNames().map((series, i) => (
          <Bar
            key={series.key}
            stackId="a"
            dataKey={series.key}
            name={formatTimeString(columnTitleWithoutView(series))}
            fill={colors[i]}
          />
        ))}
      </CartesianChart>
    );
  },
  area: ({ resultSet }) => {
    return (
      <CartesianChart resultSet={resultSet} ChartComponent={AreaChart}>
        {resultSet.seriesNames().map((series, i) => (
          <Area
            key={series.key}
            stackId="a"
            dataKey={series.key}
            name={formatTimeString(columnTitleWithoutView(series))}
            stroke={colors[i]}
            fill={colors[i]}
          />
        ))}
      </CartesianChart>
    );
  },
  pie: ({ resultSet }) => {
    return (
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            isAnimationActive={false}
            data={formatChartPivot(resultSet.chartPivot())}
            nameKey="x"
            dataKey={resultSet.seriesNames()[0].key}
            fill="#8884d8"
          >
            {formatChartPivot(resultSet.chartPivot()).map((e, index) => (
              <Cell key={index} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Legend />
          <Tooltip formatter={getTooltipFormatter(resultSet)} />
        </PieChart>
      </ResponsiveContainer>
    );
  },
  table: ({ resultSet }) => {
    return <TableRenderer resultSet={resultSet} />;
  },
};
const TypeToMemoChartComponent = Object.keys(TypeToChartComponent)
  .map((key) => ({
    [key]: React.memo(TypeToChartComponent[key]),
  }))
  .reduce((a, b) => ({
    ...a,
    ...b,
  }));
const renderChart =
  (Component) =>
    ({ resultSet, error, pivotConfig }) =>
      (resultSet && (
        <Component resultSet={resultSet} pivotConfig={pivotConfig} />
      )) ||
      (error && error.toString()) || <Spin />;
// Wrapper component that conditionally executes the query
const ConditionalQueryRenderer = ({ query, shouldExecute, onQueryStateChange, onCacheResult, children }) => {
  // CRITICAL: Only call useCubeQuery when shouldExecute is true
  // When false, we still need to call the hook (React rules), but we pass null and skip: true
  const queryToUse = shouldExecute && query ? query : null;
  const renderProps = useCubeQuery(queryToUse, {
    skip: !shouldExecute || !query,
  });

  // Report query state changes to parent only when executing
  React.useEffect(() => {
    if (shouldExecute && onQueryStateChange) {
      onQueryStateChange(renderProps);
    }
  }, [shouldExecute, renderProps, onQueryStateChange]);

  // Cache results when they arrive
  React.useEffect(() => {
    if (shouldExecute && renderProps?.resultSet && !renderProps?.error && onCacheResult) {
      onCacheResult(renderProps.resultSet);
    }
  }, [shouldExecute, renderProps?.resultSet, renderProps?.error, onCacheResult]);

  return children(renderProps);
};

const ChartRenderer = ({ vizState, shouldExecute = false, onQueryStateChange }) => {
  const { query, chartType } = vizState;

  const cachedResultsRef = React.useRef(null);
  const cachedQueryRef = React.useRef(null);
  const cachedChartTypeRef = React.useRef(null);

  // Check if query actually changed (not just shouldExecute flag)
  const queryString = query ? JSON.stringify(query) : '';
  const queryChanged = cachedQueryRef.current && cachedQueryRef.current !== queryString;

  // Clear cache if query changed (but don't update cachedQueryRef until we have new results)
  React.useEffect(() => {
    if (queryChanged) {
      cachedResultsRef.current = null;
    }
  }, [queryString]);

  const component = TypeToMemoChartComponent[chartType];

  // Cache results callback - called when query executes successfully
  // MUST be defined before any early returns to follow React hooks rules
  const handleCacheResult = React.useCallback((resultSet) => {
    cachedResultsRef.current = {
      resultSet,
      error: null,
    };
    cachedQueryRef.current = queryString;
    cachedChartTypeRef.current = chartType;
  }, [queryString, chartType]);

  // If query shouldn't execute but we have cached results, show them
  if (!shouldExecute) {
    if (cachedResultsRef.current && cachedQueryRef.current === queryString) {
      const cachedComponent = TypeToMemoChartComponent[cachedChartTypeRef.current];
      return (
        cachedComponent &&
        renderChart(cachedComponent)({
          resultSet: cachedResultsRef.current.resultSet,
          error: null,
        })
      );
    }
    // No cached results, show placeholder
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <p>Click "Run Query" to execute the chart</p>
      </div>
    );
  }

  // Only execute query when shouldExecute is true
  return (
    <ConditionalQueryRenderer
      query={query}
      shouldExecute={shouldExecute}
      onQueryStateChange={onQueryStateChange}
      onCacheResult={handleCacheResult}
    >
      {(renderProps) => {
        return (
          component &&
          renderChart(component)({
            ...renderProps,
          })
        );
      }}
    </ConditionalQueryRenderer>
  );
};
ChartRenderer.propTypes = {
  vizState: PropTypes.object,
  cubejsApi: PropTypes.object,
  shouldExecute: PropTypes.bool,
  onQueryStateChange: PropTypes.func,
};
ChartRenderer.defaultProps = {
  vizState: {},
  cubejsApi: null,
  shouldExecute: false,
  onQueryStateChange: null,
};
export default ChartRenderer;
